import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { ShieldCheckIcon as ShieldCheck, CheckCircleIcon as CheckCircle } from "@phosphor-icons/react";
import { WaxSeal } from "@/components/luxury";

/**
 * Buyer-facing verification progress badge.
 *
 * Renders one of three states based on `/api/public/kennel-verification/{id}`:
 *   • verified     — gold WaxSeal medallion + "Verified kennel" label
 *   • in_review    — warm pebbled card showing N / 7 steps with a thin gold progress bar
 *   • untouched    — nothing (we don't shame kennels that haven't started)
 *
 * Designed for kennel detail pages and public kennel cards. No admin
 * internals leak — only the counts and the conferred-seal flag.
 */
export default function VerificationProgressBadge({ breederId, compact = false }) {
  const [state, setState] = useState(null);

  useEffect(() => {
    if (!breederId) return;
    let cancelled = false;
    api.get(`/public/kennel-verification/${breederId}`)
      .then((r) => { if (!cancelled) setState(r.data); })
      .catch(() => { if (!cancelled) setState(null); });
    return () => { cancelled = true; };
  }, [breederId]);

  if (!state) return null;
  const { verified, passed_count: passed, total_steps: total, in_review } = state;

  // Verified — full gold seal
  if (verified) {
    return (
      <div
        className={`inline-flex items-center gap-2 ${compact ? "text-xs" : "text-sm"}`}
        data-testid="verification-badge-verified"
      >
        <WaxSeal size={compact ? 22 : 28} title="Verified kennel" />
        <span className="font-bold uppercase tracking-[0.2em] text-[10px] text-[#8A6A00]">
          Verified kennel
        </span>
      </div>
    );
  }

  // Not verified, no review started — render nothing.
  if (!in_review) return null;

  // Mid-review — show progress
  const pct = Math.round((passed / Math.max(total, 1)) * 100);
  return (
    <div
      className="inline-flex items-center gap-3 bg-cream border border-[#E2DAC2] rounded-full px-3 py-1.5"
      data-testid="verification-badge-in-review"
      title={`${passed} of ${total} verification steps passed`}
    >
      <ShieldCheck size={compact ? 14 : 16} weight="duotone" className="text-[#8A6A00]" />
      <div className="flex items-center gap-2">
        <span className="font-bold uppercase tracking-[0.18em] text-[10px] text-[#8A6A00]">
          Verification in review
        </span>
        <span className="font-tabular text-[11px] text-[#6B6B6B]">
          {passed} / {total}
        </span>
      </div>
      {!compact && (
        <div className="relative h-1 w-16 bg-[#EBE2C7] rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gold-gradient rounded-full transition-[width] duration-700"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}
